import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../app/store";
import { getTokens } from "../utils/localStorage";
import {
  getDecisionStatuses,
  getOrganizationBySlug,
} from "./organization/organizationSlice";
import { loginByRefreshToken } from "./users/myUserSlice";

interface ApplicationStatusState {
  isInitialized: boolean;
  isAuthenticated: boolean;
}

const initialState: ApplicationStatusState = {
  isInitialized: false,
  isAuthenticated: false,
};

async function getRefreshToken(): Promise<{
  refreshToken: string | null;
  isFromMagicLink: boolean;
}> {
  const locationSearch = new URLSearchParams(window.location.search);
  const hashToken = locationSearch.get("hash");

  if (hashToken) {
    // Clean up URL before attempting login
    window.history.replaceState(
      {},
      document.title,
      window.location.href
        .replace(`hash=${hashToken}`, "")
        .replace(/[\?&]$/, "")
    );
    return { refreshToken: hashToken, isFromMagicLink: true };
  }

  const token = getTokens();
  if (token) {
    return { refreshToken: token.refresh, isFromMagicLink: false };
  }
  return { refreshToken: null, isFromMagicLink: false };
}

export const initializeApp = createAsyncThunk(
  "applicationStatusState/initializeApp",
  async (id: string, thunkApi): Promise<ApplicationStatusState> => {
    await thunkApi.dispatch(getOrganizationBySlug(id));

    const { refreshToken, isFromMagicLink } = await getRefreshToken();
    if (!refreshToken) return { isAuthenticated: false, isInitialized: true };

    const res: any = await thunkApi.dispatch(
      loginByRefreshToken({ refreshToken, isFromMagicLink })
    );

    if (res.error) {
      return { isAuthenticated: false, isInitialized: true };
    }

    await thunkApi.dispatch(getDecisionStatuses(id));
    return { isAuthenticated: true, isInitialized: true };
  }
);

export const applicationStatusSlice = createSlice({
  name: "applicationStatus",
  initialState,
  reducers: {
    setAuthorization(state, action: PayloadAction<boolean>) {
      state.isAuthenticated = action.payload;
    },
  },
  extraReducers: {
    [initializeApp.fulfilled.type]: (
      state,
      { payload }: PayloadAction<ApplicationStatusState>
    ) => {
      state.isInitialized = payload.isInitialized;
      state.isAuthenticated = payload.isAuthenticated;
    },
  },
});

export const selectApplicationStatus = (state: RootState) =>
  state.applicationStatus;

export default applicationStatusSlice.reducer;
